import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { DeskGuest } from '@/api/pending'
import { ApiError } from '@/api/errors'
import { queryKeys } from '@/api/queryKeys'
import { deskTitle, itemsLabel, storageLabel, type Batch } from '@/lib/desk'
import { ago } from '@/lib/format'
import { Button, Card, CodeInput, Icon, Modal, Row, useToast } from '@/ui'
import { previewCode, releaseCode } from './api'
import { DeskIcon } from './DeskIcon'
import styles from './desk.module.css'
import { deskStrings } from './strings'

export type HandoverSubject = Batch | DeskGuest

interface HandoverModalProps {
  condoId: number
  subject: HandoverSubject
  /** Set by the code-first lookup: the code was already checked, so it is not typed again. */
  verifiedCode?: string
  onClose: () => void
}

const subjectIds = (s: HandoverSubject) => (s.kind === 'batch' ? s.items.map((i) => i.id) : [s.id])

export function HandoverModal({ condoId, subject, verifiedCode, onClose }: HandoverModalProps) {
  const qc = useQueryClient()
  const toast = useToast()
  const [code, setCode] = useState(verifiedCode ?? '')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const isGuest = subject.kind === 'guest'
  const t = deskStrings.handover
  const refresh = () => qc.invalidateQueries({ queryKey: queryKeys.desk.all(condoId) })

  async function submit() {
    setBusy(true)
    setError('')
    try {
      if (!verifiedCode) {
        // A typed code could belong to another unit: make sure it opens this very row.
        const preview = await previewCode(condoId, code)
        const mine = subjectIds(subject)
        if (!preview.item_ids.some((id) => mine.includes(id))) {
          setError(deskStrings.codeLookup.invalid)
          return
        }
      }
      await releaseCode(condoId, code)
      await refresh()
      toast(
        isGuest
          ? `Entrada de ${subject.guest_name} confirmada`
          : `Entregue para ${subject.resident}`,
      )
      onClose()
    } catch (e) {
      if (e instanceof ApiError && e.status === 400) setError(deskStrings.codeLookup.invalid)
      else if (e instanceof ApiError && e.status === 409) {
        setError(deskStrings.codeLookup.used)
        void refresh()
      } else setError(t.failed)
    } finally {
      setBusy(false)
    }
  }

  const title = isGuest
    ? `Confirmar entrada de ${subject.guest_name}`
    : `Entregar ${itemsLabel(subject.count)}`

  return (
    <Modal
      width={560}
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button disabled={code.length < 6} loading={busy} onClick={() => void submit()}>
            {isGuest ? t.confirmGuest : t.confirmItems}
          </Button>
        </>
      }
    >
      <div className={styles.subject}>
        <DeskIcon entry={subject} size={44} />
        <div>
          <div className={styles.subjectTitle}>
            {isGuest
              ? subject.guest_name
              : `Unidade ${subject.unit} · ${itemsLabel(subject.count)}`}
          </div>
          <div className={styles.subjectMeta}>
            {subject.tower} · {subject.resident}
          </div>
        </div>
      </div>

      {!isGuest && (
        <Card pad={0}>
          {subject.items.map((it) => {
            const parts = [
              ago(it.created_at),
              it.photo ? 'com foto' : '',
              it.storage_location ? storageLabel(it.storage_location, it.locker_slot) : '',
            ].filter(Boolean)
            return (
              <Row key={it.id} className={styles.itemRow}>
                <div className={styles.itemBody}>
                  <div className={styles.primaryText}>{deskTitle(it)}</div>
                  <div className={styles.subText}>{parts.join(' · ')}</div>
                </div>
              </Row>
            )
          })}
        </Card>
      )}

      {verifiedCode ? (
        <>
          <div className={styles.verified}>
            <span className={styles.verifiedIcon}>
              <Icon name="check" size={20} />
            </span>
            <span>
              {t.verified} <span className={styles.verifiedCode}>{verifiedCode}</span>
            </span>
          </div>
          {error && (
            <div className={styles.error} role="alert">
              <Icon name="error" size={16} />
              {error}
            </div>
          )}
        </>
      ) : (
        <CodeInput
          autoFocus
          value={code}
          onChange={(v) => {
            setCode(v)
            setError('')
          }}
          hint={t.codeHint}
          error={error || undefined}
        />
      )}
    </Modal>
  )
}
